import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, Check, AlertCircle } from 'lucide-react';
import { api } from '../contexts/AuthContext';

const ImageUploader = ({ 
  onImageUploaded, 
  currentImage = '', 
  currentAltText = '' 
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [altText, setAltText] = useState(currentAltText || '');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [imageInfo, setImageInfo] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(currentImage || null);
    setAltText(currentAltText || '');
  }, [currentImage, currentAltText]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image valide');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("L'image est trop grande (max 10MB)");
      return;
    }

    // Show immediate preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    await uploadImage(file);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await api.post('/api/blog/upload-image?generate_alt=true', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      clearInterval(progressInterval);

      if (response.data) {
        setProgress(100);
        
        const result = response.data;
        setPreview(result.url);
        setAltText(result.altText || '');
        setImageInfo({
          width: result.width,
          height: result.height,
          size: result.size,
          format: result.format
        });
        
        // Notify parent
        onImageUploaded({
          url: result.url,
          altText: result.altText || '',
          width: result.width,
          height: result.height,
        });

        setTimeout(() => {
          setProgress(0);
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'upload");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setPreview(null);
    setAltText('');
    setImageInfo(null);
    
    // Notify parent
    onImageUploaded({
      url: '',
      altText: '',
      width: 0,
      height: 0,
    });
  };

  const handleAltTextChange = (e) => {
    const newAltText = e.target.value;
    setAltText(newAltText);
    
    // Notify parent of alt text change
    if (preview) {
      onImageUploaded({
        url: preview,
        altText: newAltText,
        width: imageInfo?.width || 0,
        height: imageInfo?.height || 0,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
        {!preview ? (
          <div className="text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="image-upload-input"
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex flex-col items-center gap-3 w-full"
              data-testid="image-upload-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                  <div className="w-full max-w-xs">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {progress < 90 ? 'Upload en cours...' : 'Génération du texte alt...'} {progress}%
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Cliquez pour uploader une image
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PNG, JPG, WebP jusqu&apos;à 10MB
                    </p>
                  </div>
                </>
              )}
            </button>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative group">
              <img
                src={preview.startsWith('data:') ? preview : `${process.env.REACT_APP_BACKEND_URL}${preview}`}
                alt={altText || 'Preview'}
                className="w-full h-64 object-cover rounded-lg"
                data-testid="image-preview"
              />
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                data-testid="image-remove-button"
              >
                <X size={20} />
              </button>
            </div>

            {/* Alt Text with AI generation badge */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Alt Text (généré automatiquement)
                </label>
                {altText && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <Check size={12} />
                    Généré par IA
                  </span>
                )}
              </div>
              <textarea
                value={altText}
                onChange={handleAltTextChange}
                placeholder="Description de l'image pour l'accessibilité et le SEO..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                data-testid="alt-text-input"
              />
              <p className="text-xs text-gray-500">
                Vous pouvez modifier le texte alt généré automatiquement
              </p>
            </div>

            {/* Image info */}
            {imageInfo && (
              <div className="flex items-center gap-4 text-xs text-gray-600 pt-2 border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <ImageIcon size={14} />
                  <span>Format: {imageInfo.format?.toUpperCase() || 'WebP'}</span>
                </div>
                {imageInfo.width && imageInfo.height && (
                  <span>{imageInfo.width}x{imageInfo.height}px</span>
                )}
                {imageInfo.size && (
                  <span>{formatFileSize(imageInfo.size)}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
